import { useState } from 'react';
import { useHistory } from 'react-router';
import { toApiBoolean } from '@liquidweb-dev/club-sauce/types/boolean';
import Alert, { alertClasses } from '@liquidweb-dev/undercurrent/Alert';
import Button from '@liquidweb-dev/undercurrent/Button';
import Divider from '@liquidweb-dev/undercurrent/Divider';
import LoaderCircleIcon from '@liquidweb-dev/undercurrent/icons/animated/LoaderCircle';
import ArrowLeftIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowLeft';
import Stack from '@liquidweb-dev/undercurrent/Stack';
import TextField from '@liquidweb-dev/undercurrent/TextField';
import toast from '@liquidweb-dev/undercurrent/toast';
import Typography from '@liquidweb-dev/undercurrent/Typography';
import HookedSubmit from 'components/Form/HookedSubmit';
import LWForm, { type FormSubmitHandler } from 'components/LWForm';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { AssetDetails } from 'modules/queries/asset/useDetails';
import useAssetRemove from 'modules/queries/asset/useRemove';
import CancellationLoader from './CancellationLoader';
import { CancellationFormData } from './constants';

dayjs.extend(localizedFormat);

export interface FastCancellationProps {
	uniqId: string;
	formValues: CancellationFormData;
	assetDetails: AssetDetails | undefined;
	isAssetDetailsLoading: boolean;
	onFormValuesChange: (values: CancellationFormData) => void;
	onSubmit: FormSubmitHandler<CancellationFormData>;
	onBack: () => void;
}

const FastCancellation = ({
	uniqId,
	formValues,
	assetDetails,
	isAssetDetailsLoading,
	onFormValuesChange,
	onSubmit,
	onBack,
}: FastCancellationProps) => {
	const history = useHistory();
	const { isLoading, mutate: removeAsset } = useAssetRemove();
	const [confirmationText, setConfirmationText] = useState('');

	const handleSubmit = () => {
		if (!assetDetails?.active) {
			toast.danger('Cannot remove an asset that is not "Active"');
			return;
		}

		removeAsset(
			{
				uniq_id: uniqId,
				cancellation_reason: formValues.cancellation_reason,
				cancellation_comment:
					formValues.additional_info.length > 0
						? formValues.additional_info
						: undefined,
				cleanup_assets: toApiBoolean(formValues.cleanup_assets),
			},
			{
				onSuccess: () => {
					onSubmit(formValues);
					history.replace('/servers');
					toast.success('Service cancelled', {
						description: `Service for ${assetDetails?.domain} has been cancelled`,
					});
				},
			},
		);
	};

	if (isAssetDetailsLoading) {
		return (
			<Stack
				sx={{
					width: '100%',
				}}
			>
				<CancellationLoader
					title="Fetching your details..."
					icon={<LoaderCircleIcon />}
				/>
			</Stack>
		);
	}

	return (
		<LWForm
			values={formValues}
			onValuesChange={onFormValuesChange}
			onSubmit={handleSubmit}
		>
			<Stack sx={{ gap: 200 }}>
				<Stack sx={{ gap: 300 }}>
					<Alert
						severity="error"
						showIcon={false}
						sx={{
							paddingY: 200,
							[`& .${alertClasses.message}`]: {
								padding: 0,
							},
						}}
					>
						<Typography variant="body-sm" sx={{ fontWeight: 400 }}>
							This will permanently delete <b>{assetDetails?.domain}</b>,
							including all storage volumes and add-ons.{' '}
							<b>This cannot be undone.</b>
						</Typography>
					</Alert>
					<Typography variant="body-sm">
						By clicking Destroy server now, you agree that the service and
						add-ons associated with the server above will be{' '}
						<Typography
							variant="body-sm"
							component="span"
							sx={{ fontWeight: 500 }}
						>
							cancelled on {dayjs().format('LL')}.
						</Typography>{' '}
						Any billing for this service will be stopped and any other services
						associated with this account will not be affected.
					</Typography>
					<Divider sx={{ paddingY: 50 }} />
					<TextField
						label="To verify, type DESTROY below"
						size="medium"
						onChange={(event) => setConfirmationText(event.target.value)}
						autoComplete="off"
					/>
				</Stack>
				<Stack sx={{ gap: 100 }}>
					<HookedSubmit
						color="danger"
						variant="primary"
						size="large"
						fullWidth
						loading={isLoading}
						disabled={confirmationText !== 'DESTROY' || isLoading}
					>
						Destroy server now
					</HookedSubmit>
					<Button
						variant="secondary"
						size="large"
						fullWidth
						startIcon={<ArrowLeftIcon />}
						onClick={onBack}
					>
						Back
					</Button>
				</Stack>
			</Stack>
		</LWForm>
	);
};

export default FastCancellation;
